import {
  BLOCK_LAYOUTS,
  type BlockLayout,
  checkTemplateBlocks,
  groupsForType,
  nextTemplateVersion,
  type ProfileTemplateDef,
  resolveTemplateBlocks,
  type SchemaSnapshot,
  type TemplateBlock,
} from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { LayoutTemplate, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '../components/guidance/empty-state';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { CheckRow, EditorError, LabeledInput, Panel, SelectRow, TextRow } from '../components/schema/editor-kit';
import { Button } from '../components/ui/button';
import { useSchema, useSchemaEdit } from '../lib/schema';

export const Route = createFileRoute('/admin/templates')({
  validateSearch: (s: Record<string, unknown>): { type?: string } => (typeof s.type === 'string' ? { type: s.type } : {}),
  component: TemplatesPage,
});

const LAYOUT_LABELS: Record<BlockLayout, string> = {
  header: 'Header (code, name, badges)',
  fields: 'Fields',
  table: 'Table',
  ratings: 'Ratings',
  text: 'Text sections',
};

const lines = (s: string) =>
  s
    .split('\n')
    .map(x => x.trim())
    .filter(Boolean);

const TOUR = {
  id: 'admin-templates',
  steps: [
    {
      target: '[data-tour="template-list"]',
      title: 'Profile templates',
      body: 'A template lays out a type’s profile page, the importer and the DOCX/PDF export as ordered blocks.',
      learnMore: 'schema-and-templates',
    },
    {
      target: '[data-tour="blocks"]',
      title: 'Blocks',
      body: 'Each block shows a field group. Heading patterns tell the importer where the block starts in a sheet.',
    },
    {
      target: '[data-tour="save-version"]',
      title: 'Versions',
      body: 'Saving creates the next version (2.0 → 2.1). Earlier versions stay, so sheets made with them still import, and you can switch back.',
    },
  ],
};

function TemplatesPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const { type: typeKey } = Route.useSearch();
  if (!snap) return <PageHeader title="Profile templates" description="Loading…" />;
  const withTemplates = snap.types.filter(t => snap.templates.some(x => x.entityTypeId === t.id));
  const type = withTemplates.find(t => t.key === (typeKey ?? 'actor')) ?? withTemplates[0];
  const versions = snap.templates.filter(t => t.entityTypeId === type?.id).sort((a, b) => b.createdAt - a.createdAt);
  const active = versions.find(v => v.active) ?? versions[0];
  return (
    <>
      <PageHeader
        title="Profile templates"
        description="How profiles are laid out, imported and exported: the Actor Profile Sheet v2.0 and its variants."
        actions={
          <Button size="sm" variant="outline" asChild>
            <Link to="/admin/schema">Types & fields</Link>
          </Button>
        }
      />
      <Tip id="admin-templates" learnMore="schema-and-templates">
        Variants (Person, Organization…) inherit the base template’s blocks and add or remove their own. When you save a new version of the
        base, its variants follow automatically.
      </Tip>
      <div className="mt-4 grid gap-6 lg:grid-cols-[17rem_1fr]">
        <nav aria-label="Templates" data-tour="template-list">
          <ul className="space-y-0.5">
            {withTemplates.map(t => {
              const a = snap.templates.find(x => x.entityTypeId === t.id && x.active);
              return (
                <li key={t.id}>
                  <Link
                    to="/admin/templates"
                    search={{ type: t.key }}
                    aria-current={t.id === type?.id ? 'page' : undefined}
                    className="block rounded-md px-2 py-1 text-sm hover:bg-muted aria-[current=page]:bg-muted aria-[current=page]:font-medium"
                  >
                    <span className="block">{a?.name ?? t.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {t.label} · v{a?.version ?? '—'}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
        <div className="min-w-0 space-y-6">
          {active && type ? (
            <>
              <TemplateEditor key={active.id} snap={snap} template={active} versions={versions.map(v => v.version)} />
              <Versions snap={snap} versions={versions} />
            </>
          ) : (
            <EmptyState icon={LayoutTemplate} title="No templates yet" learnMore="schema-and-templates" />
          )}
        </div>
      </div>
    </>
  );
}

function Versions({ snap, versions }: { snap: SchemaSnapshot; versions: ProfileTemplateDef[] }) {
  const edit = useSchemaEdit();
  return (
    <Panel title="Versions">
      <ul className="divide-y rounded-md border text-sm" aria-label="Template versions">
        {versions.map(v => {
          const parent = snap.templates.find(p => p.id === v.parentTemplateId);
          return (
            <li key={v.id} className="flex flex-wrap items-center gap-2 p-2" aria-label={`Version ${v.version}`}>
              <span className="font-medium tabular">v{v.version}</span>
              {v.active && <span className="rounded bg-muted px-1.5 text-xs">In use</span>}
              {parent && (
                <span className="text-xs text-muted-foreground">
                  inherits {parent.name} v{parent.version}
                </span>
              )}
              <span className="text-xs text-muted-foreground tabular">{new Date(v.createdAt).toISOString().slice(0, 10)}</span>
              {v.notes && <span className="text-xs text-muted-foreground">— {v.notes}</span>}
              {!v.active && (
                <Button
                  size="sm"
                  variant="outline"
                  className="ms-auto"
                  onClick={() => edit.mutate({ path: `/admin/schema/templates/${v.id}/activate` })}
                >
                  Use this version
                </Button>
              )}
            </li>
          );
        })}
      </ul>
      <EditorError error={edit.error} />
    </Panel>
  );
}

function TemplateEditor({ snap, template, versions }: { snap: SchemaSnapshot; template: ProfileTemplateDef; versions: string[] }) {
  const edit = useSchemaEdit();
  const [blocks, setBlocks] = useState<TemplateBlock[]>(template.blocks);
  const [removed, setRemoved] = useState<string[]>(template.removedBlocks);
  const [markings, setMarkings] = useState(template.boilerplateMarkings.join('\n'));
  const [notes, setNotes] = useState('');
  const [saved, setSaved] = useState<string | null>(null);
  const parent = snap.templates.find(t => t.id === template.parentTemplateId);
  const inherited = parent ? resolveTemplateBlocks(snap.templates, parent.id) : [];
  const groups = groupsForType(snap, template.entityTypeId);
  const problems = checkTemplateBlocks(blocks, snap, template.entityTypeId);
  const next = nextTemplateVersion(versions);
  const set = (i: number, p: Partial<TemplateBlock>) => setBlocks(bs => bs.map((b, j) => (j === i ? { ...b, ...p } : b)));
  const draft: ProfileTemplateDef = { ...template, id: '__draft', blocks, removedBlocks: removed };
  const preview = resolveTemplateBlocks([...snap.templates, draft], '__draft');
  const groupOptions = [{ value: '', label: '(no fields)' }, ...groups.map(g => ({ value: g.key, label: g.label }))];
  return (
    <>
      <Panel
        title={
          <span>
            {template.name} <span className="font-normal text-muted-foreground tabular">v{template.version}</span>
          </span>
        }
      >
        {parent && (
          <div className="space-y-2">
            <h3 className="text-sm font-semibold">
              Inherited from {parent.name} v{parent.version}
            </h3>
            <ul className="flex flex-wrap gap-2" aria-label="Inherited blocks">
              {inherited.map(b => (
                <li key={b.key}>
                  <CheckRow
                    label={`${b.number ? `${b.number} · ` : ''}${b.label}`}
                    checked={!removed.includes(b.key)}
                    onChange={keep => setRemoved(r => (keep ? r.filter(k => k !== b.key) : [...r, b.key]))}
                  />
                </li>
              ))}
            </ul>
            <p className="text-xs text-muted-foreground">Untick a block to leave it out of this variant.</p>
          </div>
        )}
        <div data-tour="blocks" className="space-y-2">
          <h3 className="text-sm font-semibold">{parent ? 'Blocks added by this variant' : 'Blocks'}</h3>
          <ul className="space-y-2">
            {blocks.map((b, i) => (
              <li key={b.key || i} className="rounded-md border p-2" aria-label={`Block ${b.label || i + 1}`}>
                <div className="grid gap-2 sm:grid-cols-[4.5rem_1fr_1fr_1fr_auto] sm:items-end">
                  <LabeledInput
                    label="Order"
                    type="number"
                    value={b.order}
                    onChange={e => set(i, { order: Number(e.target.value) })}
                    className="tabular"
                  />
                  <LabeledInput label="Label" value={b.label} onChange={e => set(i, { label: e.target.value })} />
                  <SelectRow
                    label="Field group"
                    value={b.group ?? ''}
                    options={groupOptions}
                    onChange={v => set(i, { group: v || null })}
                  />
                  <SelectRow
                    label="Layout"
                    value={b.layout}
                    options={BLOCK_LAYOUTS.map(l => ({ value: l, label: LAYOUT_LABELS[l] }))}
                    onChange={v => set(i, { layout: v })}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={`Remove block ${b.label}`}
                    onClick={() => setBlocks(bs => bs.filter((_, j) => j !== i))}
                  >
                    <Trash2 aria-hidden="true" />
                  </Button>
                </div>
                <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                  <label className="space-y-1 text-xs">
                    <span className="font-medium">Import headings (one pattern per line)</span>
                    <textarea
                      rows={2}
                      value={(b.import?.headingPatterns ?? []).join('\n')}
                      placeholder={'e.g. ^block\\s*4\\b\ninfluence ratings'}
                      onChange={e => set(i, { import: { ...b.import, headingPatterns: e.target.value.split('\n') } })}
                      onBlur={e => set(i, { import: { ...b.import, headingPatterns: lines(e.target.value) } })}
                      className="w-full rounded-md border border-input bg-surface px-3 py-1.5 font-mono text-sm"
                    />
                  </label>
                  <CheckRow
                    label="Ignored"
                    checked={!!b.ignored}
                    onChange={v => set(i, { ignored: v })}
                    help="Skipped on import and never shown (like Block 9, the dropped composite score)."
                  />
                </div>
              </li>
            ))}
          </ul>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() =>
              setBlocks(bs => [
                ...bs,
                {
                  key: `block_${bs.length + 1}`,
                  label: 'New block',
                  order: Math.max(0, ...preview.map(b => b.order)) + 10,
                  layout: 'fields',
                  group: null,
                },
              ])
            }
          >
            <Plus aria-hidden="true" /> Add block
          </Button>
        </div>
        <TextRow
          label="Boilerplate markings"
          multiline
          value={markings}
          onChange={setMarkings}
          placeholder="e.g. CONFIDENTIAL — Internal use only"
          help="Header markings that are part of the template, one per line. The upload marking check won't ask about them for every sheet."
        />
        {problems.length > 0 && (
          <ul className="list-inside list-disc text-xs text-warning-foreground">
            {problems.map(p => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}
        <div data-tour="save-version" className="flex flex-wrap items-end gap-2">
          <div className="w-72">
            <TextRow label="What changed" value={notes} onChange={setNotes} placeholder="e.g. Funding block moved up" />
          </div>
          <Button
            size="sm"
            disabled={problems.length > 0 || edit.isPending}
            onClick={() =>
              edit.mutate(
                {
                  path: `/admin/schema/templates/${template.id}/versions`,
                  body: { blocks, removedBlocks: removed, boilerplateMarkings: lines(markings), notes: notes || null },
                },
                {
                  onSuccess: d => {
                    const out = d as { version?: string; created?: { version: string }[] };
                    setSaved(
                      `Saved v${out.version}${out.created && out.created.length > 1 ? ` (${out.created.length - 1} variant(s) updated)` : ''}.`,
                    );
                  },
                },
              )
            }
          >
            Save as v{next}
          </Button>
          {saved && (
            <p role="status" className="text-sm text-success-foreground">
              {saved}
            </p>
          )}
        </div>
        <EditorError error={edit.error} />
      </Panel>
      <Panel title="Layout preview">
        <ol className="space-y-1 text-sm" aria-label="Profile layout">
          {preview.map(b => {
            const fields = groups.find(g => g.key === b.group)?.fields ?? [];
            return (
              <li key={b.key} className={b.ignored ? 'text-muted-foreground line-through' : ''}>
                <span className="font-medium">
                  {b.number ? `Block ${b.number} · ` : ''}
                  {b.label}
                </span>
                {fields.length > 0 && <span className="text-muted-foreground"> — {fields.map(f => f.label).join(', ')}</span>}
                {b.storedAs && <span className="block text-xs text-muted-foreground">Stored as: {b.storedAs}</span>}
              </li>
            );
          })}
        </ol>
      </Panel>
    </>
  );
}
